import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBIcon,
  MDBModal,
  MDBModalHeader,
  MDBModalBody,
  MDBModalFooter,
} from 'mdbreact';

import NavBar from '../../../components/NavBar';
import api from '../../../services/api';

export default function ImportarAutosInfracao() {
  const history = useHistory();
  const fileInputRef = useRef(null);

  const [arquivo, setArquivo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fase, setFase] = useState('');
  const [progresso, setProgresso] = useState(0);
  const [resultado, setResultado] = useState(null);
  const [dragAtivo, setDragAtivo] = useState(false);
  const [limparDados, setLimparDados] = useState(false);
  // #211: confirmação da substituição total da base
  const [confirmandoLimpeza, setConfirmandoLimpeza] = useState(false);

  const validarArquivo = (file) => {
    if (!file) return false;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Apenas arquivos CSV são permitidos');
      return false;
    }
    if (file.size > 500 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Tamanho máximo: 500MB');
      return false;
    }
    return true;
  };

  const handleArquivoSelecionado = (event) => {
    const file = event.target.files[0];
    if (validarArquivo(file)) {
      setArquivo(file);
      setResultado(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragAtivo(false);
    const file = e.dataTransfer.files[0];
    if (validarArquivo(file)) {
      setArquivo(file);
      setResultado(null);
    }
  };

  // #211: substituir a base é irreversível e não tinha cerimônia nenhuma — o
  // checkbox marcado ia direto para o servidor junto com o arquivo. Aqui o
  // clique em Importar só abre a confirmação; quem executa é o modal.
  const handleImportar = () => {
    if (!arquivo) {
      toast.error('Selecione um arquivo CSV');
      return;
    }

    if (limparDados) {
      setConfirmandoLimpeza(true);
      return;
    }

    executarImportacao();
  };

  const executarImportacao = async () => {
    setConfirmandoLimpeza(false);
    setLoading(true);
    setFase('Enviando arquivo...');
    setProgresso(0);
    setResultado(null);

    try {
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      if (limparDados) {
        formData.append('limpar_anterior', 'true');
      }

      setFase('Enviando arquivo ao servidor...');
      const response = await api.post('/auto-infracao-receita/importar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgresso(pct);
          if (pct >= 100) {
            setFase('Processando registros no servidor...');
          }
        },
      });

      if (response.data.sucesso) {
        toast.success('Importação concluída com sucesso!');
        setResultado(response.data);
        setArquivo(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        toast.error('Erro na importação');
      }
    } catch (error) {
      console.error('Erro ao importar:', error);
      toast.error(
        error.response?.data?.erro ||
        error.response?.data?.mensagem ||
        'Erro ao processar arquivo'
      );
    } finally {
      setLoading(false);
      setFase('');
      setProgresso(0);
    }
  };

  const handleCancelar = () => {
    setArquivo(null);
    setResultado(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatarTamanho = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  return (
    <>
      <NavBar />
      <MDBContainer className="mt-4">
        <MDBRow className="mb-3">
          <MDBCol md="12">
            <h4 className="font-weight-bold" style={{ color: '#2c3e50' }}>
              <MDBIcon icon="upload" className="mr-2 text-primary" />
              Importar Autos de Infração
              <small className="text-muted ml-2" style={{ fontSize: '14px' }}>
                Receita Estadual
              </small>
            </h4>
            <hr style={{ borderTop: '2px solid #3f51b5' }} />
          </MDBCol>
        </MDBRow>

        <MDBRow>
          <MDBCol md="8">
            <MDBCard style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: '8px' }}>
              <MDBCardBody>
                {/* Opção de limpar dados anteriores */}
                <div className="custom-control custom-checkbox mb-3">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="limparDadosCheckbox"
                    checked={limparDados}
                    onChange={(e) => setLimparDados(e.target.checked)}
                    disabled={loading}
                  />
                  <label className="custom-control-label" htmlFor="limparDadosCheckbox">
                    <strong className="text-danger">Limpar todos os dados anteriores</strong> antes de importar
                    <small className="d-block text-muted" style={{ fontSize: '12px' }}>
                      ⚠️ Esta ação irá excluir permanentemente todos os registros existentes de Autos de Infração
                    </small>
                  </label>
                </div>

                {/* Zona de Upload */}
                <div
                  onClick={() => !loading && fileInputRef.current && fileInputRef.current.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragAtivo(true); }}
                  onDragLeave={() => setDragAtivo(false)}
                  onDrop={handleDrop}
                  style={{
                    border: `2px dashed ${dragAtivo ? '#3f51b5' : arquivo ? 'var(--sig-construtiva)' : '#ccc'}`,
                    borderRadius: '10px',
                    padding: '40px 20px',
                    textAlign: 'center',
                    cursor: loading ? 'wait' : 'pointer',
                    backgroundColor: dragAtivo ? '#e8eaf6' : arquivo ? '#e8f5e9' : '#fafafa',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv"
                    onChange={handleArquivoSelecionado}
                    disabled={loading}
                    style={{ display: 'none' }}
                  />
                  {arquivo ? (
                    <>
                      <MDBIcon icon="file" size="3x" className="text-success mb-3" />
                      <p className="mb-1 font-weight-bold" style={{ fontSize: '16px' }}>
                        {arquivo.name}
                      </p>
                      <p className="text-muted mb-0">{formatarTamanho(arquivo.size)}</p>
                    </>
                  ) : (
                    <>
                      <MDBIcon icon="upload" size="3x" className="text-muted mb-3" />
                      <p className="mb-1" style={{ fontSize: '16px' }}>
                        Arraste o arquivo CSV aqui ou <strong className="text-primary">clique para selecionar</strong>
                      </p>
                      <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                        Formato: CSV separado por ponto e v&#237;rgula (;) &bull; M&#225;x: 500 MB
                      </p>
                    </>
                  )}
                </div>

                {/* Loader animado */}
                {loading && (
                  <div className="mt-4">
                    <div
                      style={{
                        background: '#f5f7fa',
                        borderRadius: '8px',
                        padding: '24px',
                        textAlign: 'center',
                      }}
                    >
                      <div className="mb-3">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                          style={{ width: '3rem', height: '3rem' }}
                        >
                          <span className="sr-only">Carregando...</span>
                        </div>
                      </div>
                      <p className="font-weight-bold mb-2" style={{ fontSize: '15px', color: '#2c3e50' }}>
                        {fase}
                      </p>
                      {/* Barra de progresso */}
                      <div
                        style={{
                          height: '8px',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          maxWidth: '400px',
                          margin: '0 auto',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: progresso >= 100 ? '100%' : `${progresso}%`,
                            backgroundColor: progresso >= 100 ? '#ff9800' : 'var(--sig-construtiva)',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease',
                            animation: progresso >= 100 ? 'pulse 1.5s infinite' : 'none',
                          }}
                        />
                      </div>
                      <p className="text-muted mt-2 mb-0" style={{ fontSize: '13px' }}>
                        {progresso >= 100
                          ? 'O servidor est\u00e1 processando os dados. Isso pode levar alguns minutos...'
                          : `Upload: ${progresso}%`}
                      </p>
                    </div>
                    <style>
                      {`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}
                    </style>
                  </div>
                )}

                {/* Resultado */}
                {resultado && (
                  <div className="mt-4">
                    {resultado.registros_excluidos > 0 && (
                      <div className="alert alert-warning mb-3" role="alert">
                        <MDBIcon icon="trash-alt" className="mr-2" />
                        <strong>{resultado.registros_excluidos?.toLocaleString('pt-BR')}</strong> registros anteriores foram excluídos antes da importação.
                      </div>
                    )}
                    <MDBRow>
                      <MDBCol md="3" className="mb-3">
                        <div
                          style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '8px',
                            padding: '16px',
                            color: '#fff',
                            textAlign: 'center',
                          }}
                        >
                          <MDBIcon icon="database" className="mb-2" size="lg" />
                          <p className="mb-0" style={{ fontSize: '12px', opacity: 0.85 }}>Total</p>
                          <h4 className="mb-0 font-weight-bold">
                            {resultado.total_registros?.toLocaleString('pt-BR')}
                          </h4>
                        </div>
                      </MDBCol>
                      <MDBCol md="3" className="mb-3">
                        <div
                          style={{
                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                            borderRadius: '8px',
                            padding: '16px',
                            color: '#fff',
                            textAlign: 'center',
                          }}
                        >
                          <MDBIcon icon="check-circle" className="mb-2" size="lg" />
                          <p className="mb-0" style={{ fontSize: '12px', opacity: 0.85 }}>Importados</p>
                          <h4 className="mb-0 font-weight-bold">
                            {resultado.registros_importados?.toLocaleString('pt-BR')}
                          </h4>
                        </div>
                      </MDBCol>
                      <MDBCol md="3" className="mb-3">
                        <div
                          style={{
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            borderRadius: '8px',
                            padding: '16px',
                            color: '#fff',
                            textAlign: 'center',
                          }}
                        >
                          <MDBIcon icon="refresh" className="mb-2" size="lg" />
                          <p className="mb-0" style={{ fontSize: '12px', opacity: 0.85 }}>Atualizados</p>
                          <h4 className="mb-0 font-weight-bold">
                            {resultado.registros_duplicados?.toLocaleString('pt-BR')}
                          </h4>
                        </div>
                      </MDBCol>
                      <MDBCol md="3" className="mb-3">
                        <div
                          style={{
                            background: resultado.registros_erro > 0
                              ? 'linear-gradient(135deg, var(--sig-destrutiva) 0%, var(--sig-destrutiva-escura) 100%)'
                              : 'linear-gradient(135deg, var(--sig-construtiva) 0%, var(--sig-construtiva-escura) 100%)',
                            borderRadius: '8px',
                            padding: '16px',
                            color: '#fff',
                            textAlign: 'center',
                          }}
                        >
                          <MDBIcon
                            icon={resultado.registros_erro > 0 ? 'exclamation-triangle' : 'thumbs-up'}
                            className="mb-2"
                            size="lg"
                          />
                          <p className="mb-0" style={{ fontSize: '12px', opacity: 0.85 }}>Erros</p>
                          <h4 className="mb-0 font-weight-bold">
                            {resultado.registros_erro?.toLocaleString('pt-BR')}
                          </h4>
                        </div>
                      </MDBCol>
                    </MDBRow>

                    <div className="text-muted text-center mb-3" style={{ fontSize: '13px' }}>
                      <MDBIcon icon="clock" className="mr-1" />
                      Processado em <strong>{resultado.tempo_processamento}</strong> segundos
                      {resultado.encoding_detectado && (
                        <span className="ml-3">
                          <MDBIcon icon="font" className="mr-1" />
                          Encoding: <strong>{resultado.encoding_detectado}</strong>
                        </span>
                      )}
                    </div>

                    {resultado.erros && resultado.erros.length > 0 && (
                      <div
                        className="mt-2 p-3"
                        style={{
                          backgroundColor: '#fff3e0',
                          borderRadius: '8px',
                          borderLeft: '4px solid #ff9800',
                          maxHeight: '200px',
                          overflowY: 'auto',
                        }}
                      >
                        <p className="font-weight-bold mb-2" style={{ fontSize: '13px' }}>
                          <MDBIcon icon="exclamation-circle" className="mr-1 text-warning" />
                          Erros encontrados ({resultado.erros.length}):
                        </p>
                        {resultado.erros.slice(0, 10).map((erro, index) => (
                          <p key={index} className="mb-1" style={{ fontSize: '12px' }}>
                            <span className="text-muted">
                              {erro.linha ? `Linha ${erro.linha}` : ''}
                              {erro.nu_ai ? ` AI ${erro.nu_ai}` : ''}:
                            </span>{' '}
                            {erro.erro}
                          </p>
                        ))}
                        {resultado.erros.length > 10 && (
                          <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
                            ... e mais {resultado.erros.length - 10} erros.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Bot\u00f5es */}
                <div className="mt-4 d-flex justify-content-between">
                  <MDBBtn
                    outline
                    color="grey"
                    size="sm"
                    onClick={handleCancelar}
                    disabled={loading}
                  >
                    <MDBIcon icon="times" className="mr-1" />
                    Limpar
                  </MDBBtn>
                  <div>
                    <MDBBtn
                      color="primary"
                      size="sm"
                      onClick={() => history.push('/dados-estatisticos/autos-infracao')}
                    >
                      <MDBIcon icon="list-alt" className="mr-1" />
                      Ver Registros
                    </MDBBtn>
                    <MDBBtn
                      color="success"
                      size="sm"
                      onClick={handleImportar}
                      disabled={!arquivo || loading}
                    >
                      <MDBIcon icon="upload" className="mr-1" />
                      {loading ? 'Importando...' : 'Importar'}
                    </MDBBtn>
                  </div>
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>

          {/* Painel lateral de informa\u00e7\u00f5es */}
          <MDBCol md="4">
            <MDBCard style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: '8px' }}>
              <MDBCardBody>
                <MDBCardTitle style={{ fontSize: '15px' }}>
                  <MDBIcon icon="info-circle" className="mr-2 text-info" />
                  Informações
                </MDBCardTitle>
                <hr />
                <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                  <p className="mb-2">
                    <MDBIcon icon="file-alt" className="mr-2 text-success" />
                    Formato: <strong>CSV</strong> (separador ;)
                  </p>
                  <p className="mb-2">
                    <MDBIcon icon="font" className="mr-2 text-primary" />
                    Encoding: <strong>auto-detectado</strong>
                  </p>
                  <p className="mb-2">
                    <MDBIcon icon="hdd" className="mr-2 text-warning" />
                    Tamanho m&#225;x: <strong>500 MB</strong>
                  </p>
                  <p className="mb-2">
                    <MDBIcon icon="refresh" className="mr-2 text-info" />
                    Duplicados: <strong>atualizados</strong>
                  </p>
                </div>

                <hr />
                <p className="font-weight-bold mb-2" style={{ fontSize: '13px' }}>
                  <MDBIcon icon="columns" className="mr-2" />
                  Colunas esperadas:
                </p>
                <div style={{ fontSize: '12px' }}>
                  {['NU_AI', 'DT_LAVRATURA', 'STATUS_AI', 'STATUS_ACOMPANHAMENTO',
                    'NU_INFRACAO', 'INFRACAO', 'RF_LAVRATURA', 'IMPOSTO', 'MULTA',
                    'MULTA_FORMAL', 'TOTAL'].map((col) => (
                    <span
                      key={col}
                      className="badge badge-light mr-1 mb-1 p-1"
                      style={{ fontWeight: 'normal', border: '1px solid #e0e0e0' }}
                    >
                      {col}
                    </span>
                  ))}
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>

        {/* #211: a substituição total da base é irreversível e não tem
            desfazer — nem no banco, que não guarda versão anterior. O modal
            existe para que ela nunca aconteça por um checkbox esquecido
            marcado de uma importação anterior. */}
        <MDBModal
          isOpen={confirmandoLimpeza}
          toggle={() => setConfirmandoLimpeza(false)}
        >
          <MDBModalHeader toggle={() => setConfirmandoLimpeza(false)}>
            Substituir toda a base de Autos de Infração?
          </MDBModalHeader>
          <MDBModalBody>
            <p className="mb-3">
              Você marcou <strong>Limpar todos os dados anteriores</strong>.
              Antes de importar{' '}
              <strong>{arquivo ? arquivo.name : 'o arquivo'}</strong>, todos os
              registros de Autos de Infração existentes serão{' '}
              <strong>excluídos definitivamente</strong>.
            </p>
            <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
              Não há como desfazer. Se quiser apenas acrescentar os registros do
              arquivo aos que já existem, cancele e desmarque a opção.
            </p>
          </MDBModalBody>
          <MDBModalFooter>
            <MDBBtn
              color="light"
              size="sm"
              onClick={() => setConfirmandoLimpeza(false)}
            >
              Cancelar
            </MDBBtn>
            <MDBBtn
              color="white"
              size="sm"
              style={{
                color: 'var(--sig-destrutiva)',
                border: '1px solid var(--sig-destrutiva)',
                boxShadow: 'none',
              }}
              onClick={executarImportacao}
            >
              Excluir tudo e importar
            </MDBBtn>
          </MDBModalFooter>
        </MDBModal>
      </MDBContainer>
    </>
  );
}
